/**
 * Reusable confirmation modal component
 * Lightweight, accessible, and easy to integrate
 */
import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';

(function() {
    'use strict';

    // Helper to get translation (falls back to key if i18n not available)
    function t(key) {
        return window.t ? window.t(key) : key;
    }

    window.ConfirmModal = {
        activeModal: null,

        /**
         * Show a confirmation modal
         * @param {Object} options Configuration options
         * @param {string} options.title Modal title
         * @param {string} options.message Confirmation message
         * @param {string} options.confirmText Button text (default: translated "Confirm")
         * @param {string} options.cancelText Button text (default: translated "Cancel")
         * @param {Function} options.onConfirm Callback when confirmed
         * @param {Function} options.onCancel Callback when cancelled
         * @param {string} options.type Modal type: danger|warning|info (default: danger)
         */
        show(options) {
            const config = Object.assign({
                title: t('common.confirm_action'),
                message: t('common.are_you_sure'),
                confirmText: t('common.confirm'),
                cancelText: t('common.cancel'),
                type: 'danger',
                onConfirm: () => {},
                onCancel: () => {}
            }, options);
            
            // Create modal if it doesn't exist
            let modal = document.getElementById('confirmModal');
            if (!modal) {
                modal = this.createModal();
                document.body.appendChild(modal);
            }
            
            // Update content
            this.updateModal(modal, config);
            
            // Show modal
            modal.classList.remove('hidden');
            this.activeModal = { modal, config };
            lockBodyScroll();

            // Focus confirm button for accessibility
            setTimeout(() => {
                const confirmBtn = modal.querySelector('[data-confirm]');
                if (confirmBtn) confirmBtn.focus();
            }, 50);
        },
        
        createModal() {
            const modal = document.createElement('div');
            modal.id = 'confirmModal';
            modal.className = 'fixed inset-0 z-50 hidden';
            modal.setAttribute('role', 'dialog');
            modal.setAttribute('aria-modal', 'true');
            
            modal.innerHTML = `
                <div class="fixed inset-0 bg-black bg-opacity-50" data-backdrop></div>
                <div class="fixed inset-0 flex items-center justify-center p-4">
                    <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
                        <div class="p-6">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full mb-4" data-icon>
                                <i class="fa fa-exclamation-triangle text-xl"></i>
                            </div>
                            <h3 class="text-lg font-semibold text-gray-900 text-center mb-2" data-title></h3>
                            <p class="text-gray-600 text-center mb-6" data-message></p>
                            <div class="flex gap-3">
                                <button type="button" class="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium" data-cancel></button>
                                <button type="button" class="flex-1 px-4 py-2 rounded-lg font-medium" data-confirm></button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            // Bind events
            this.bindModalEvents(modal);
            
            return modal;
        },
        
        updateModal(modal, config) {
            // Update text content (safely escape HTML)
            modal.querySelector('[data-title]').textContent = config.title;
            modal.querySelector('[data-message]').textContent = config.message;
            modal.querySelector('[data-cancel]').textContent = config.cancelText;
            modal.querySelector('[data-confirm]').textContent = config.confirmText;
            
            // Update styling based on type
            const iconContainer = modal.querySelector('[data-icon]');
            const confirmBtn = modal.querySelector('[data-confirm]');
            
            // Reset classes
            iconContainer.className = 'flex items-center justify-center w-12 h-12 mx-auto rounded-full mb-4';
            confirmBtn.className = 'flex-1 px-4 py-2 rounded-lg font-medium';
            
            // Apply type-specific styling
            if (config.type === 'danger') {
                iconContainer.classList.add('bg-red-100');
                iconContainer.querySelector('i').className = 'fa fa-exclamation-triangle text-red-600 text-xl';
                confirmBtn.classList.add('bg-red-500', 'text-white', 'hover:bg-red-600');
            } else if (config.type === 'warning') {
                iconContainer.classList.add('bg-yellow-100');
                iconContainer.querySelector('i').className = 'fa fa-exclamation-circle text-yellow-600 text-xl';
                confirmBtn.classList.add('bg-yellow-500', 'text-white', 'hover:bg-yellow-600');
            } else {
                iconContainer.classList.add('bg-sky-100');
                iconContainer.querySelector('i').className = 'fa fa-info-circle text-sky-600 text-xl';
                confirmBtn.classList.add('bg-sky-500', 'text-white', 'hover:bg-sky-600');
            }
        },
        
        bindModalEvents(modal) {
            // Backdrop click
            modal.querySelector('[data-backdrop]').addEventListener('click', () => this.close(false));
            
            // Cancel button
            modal.querySelector('[data-cancel]').addEventListener('click', () => this.close(false));
            
            // Confirm button
            modal.querySelector('[data-confirm]').addEventListener('click', () => this.close(true));
            
            // ESC key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.activeModal) {
                    this.close(false);
                }
            });
        },
        
        close(confirmed) {
            if (!this.activeModal) return;

            const { modal, config } = this.activeModal;

            // Hide modal
            modal.classList.add('hidden');
            unlockBodyScroll();

            // Call appropriate callback
            if (confirmed) {
                config.onConfirm();
            } else {
                config.onCancel();
            }

            this.activeModal = null;
        }
    };
})();